/**
 * Registry of the unit-type keys (naming audit step 2, 2026-10-24, Dr. Tatar).
 *
 * One list says which keys exist, which kind each is, and which other spellings are known mistakes of the same type. The type
 * tables in unitTypes.js (BATTALION_TYPES, UNIT_TYPES, SUBUNIT_ONLY_TYPES) must agree with it, every other table keyed by unit type
 * is checked against it by src/calculationEngine/__tests__/unitTypeRegistry.test.js, and the exceptions that exist today are
 * recorded in unitTypeKeyExceptions.js so that they can only shrink. NOTHING in the calculation reads this file yet (no behaviour
 * change in step 2); docs/planning/audit/NAMING_AUDIT.md has the plan.
 *
 * kinds: BRIGADE = a key of UNIT_TYPES (the type of a brigade); BATTALION = a key of BATTALION_TYPES (the type of a Tab 1 card);
 *        SUBUNIT = a key of SUBUNIT_ONLY_TYPES; LISTED = a unit type with no definition yet (named in a table, not buildable).
 */

export const UNIT_TYPE_REGISTRY = Object.freeze({
  // maneuver and fires
  INFANTRY:       { kinds: ['BRIGADE', 'BATTALION'] },
  MECHANIZED:     { kinds: ['BRIGADE', 'BATTALION'] },
  ARMOR:          { kinds: ['BRIGADE', 'BATTALION'] },
  RECONNAISSANCE: { kinds: ['BRIGADE', 'BATTALION'] },
  ARTILLERY:      { kinds: ['BRIGADE', 'BATTALION'] },
  AIR_DEFENSE:    { kinds: ['BRIGADE', 'BATTALION'] },
  AIRBORNE:       { kinds: ['BRIGADE', 'BATTALION'] },
  AIR_ASSAULT:    { kinds: ['BRIGADE', 'BATTALION'] },
  MOUNTAIN:       { kinds: ['BRIGADE', 'BATTALION'] },
  AVIATION:       { kinds: ['BRIGADE', 'BATTALION'] },
  // brigade types only
  HEAVY:          { kinds: ['BRIGADE'] },
  STRYKER:        { kinds: ['BRIGADE'] },
  // support
  ENGINEERS:      { kinds: ['BRIGADE', 'BATTALION'] },
  SIGNAL:         { kinds: ['BRIGADE', 'BATTALION'] },
  LOGISTICS:      { kinds: ['BRIGADE', 'BATTALION'] },
  MEDICAL:        { kinds: ['BRIGADE', 'BATTALION'] },
  // battalion types only
  MAINTENANCE:     { kinds: ['BATTALION'] },
  TRANSPORT:       { kinds: ['BATTALION'] },
  SUPPLY:          { kinds: ['BATTALION'] },
  MEDICAL_SUPPORT: { kinds: ['BATTALION'] },
  // the headquarters is a brigade type and the type of the HQ card of a brigade (in BATTALION_TYPES since step 4)
  HQ:             { kinds: ['BRIGADE', 'BATTALION'] },
  // sub-units only
  MILITARY_INTELLIGENCE: { kinds: ['SUBUNIT'] },
  // 2026-10-24 (Dr. Tatar): the six real functions of the unit types that were only listed, defined in BATTALION_TYPES (educated guess
  // numbers, RESEARCH_LISTED_UNIT_TYPES.md). Battalion-level only (a special forces group, an MP brigade etc. are not defined).
  SPECIAL_FORCES:    { kinds: ['BATTALION'] },
  ATTACK_AVIATION:   { kinds: ['BATTALION'] },
  MILITARY_POLICE:   { kinds: ['BATTALION'] },
  CBRN_DEFENSE:      { kinds: ['BATTALION'] },   // NATO and Romanian term (apărare CBRN); 'Chemical' is the US branch name, an alias and a label
  CIVIL_AFFAIRS:     { kinds: ['BATTALION'] },   // NATO: CIMIC
  PSYCHOLOGICAL_OPS: { kinds: ['BATTALION'] },
  // a unit type that exists but has NO definition yet: no casualty modifier, composition or strength, so it is in none of the three type
  // tables and cannot be built as a card in Tab 1 (kind LISTED). SUPPORT is the brigade type of the support-brigade template.
  SUPPORT:          { kinds: ['LISTED'] }
});

/**
 * Spellings that are NOT unit types of their own: each is a known mistake or an old name of a registry key. The value is the key
 * it means. 'ENGINEER' was merged into ENGINEERS in step 3 (2026-10-24); an old save may still carry it, see normalizeUnitTypeKey.
 * A value of null means "no such unit type, decide: add it or remove it" (naming audit section 5).
 */
export const UNIT_TYPE_ALIASES = Object.freeze({
  ENGINEER:       'ENGINEERS',
  ARMORED:        'ARMOR',
  CAVALRY:        'RECONNAISSANCE',   // only the function the code meant by it (DOCTRINAL-005); 'cavalry' is a national LABEL, never a key (NAMING_AUDIT.md section 5)
  HEADQUARTERS:   'HQ',
  TRANSPORTATION: 'TRANSPORT',
  COMMUNICATIONS: 'SIGNAL',
  NETWORK:        'SIGNAL',
  CHEMICAL:       'CBRN_DEFENSE',   // the US branch name; the function is CBRN defence
  // 2026-10-24: the other branch or class names of the research note are not functions (RESEARCH_LISTED_UNIT_TYPES.md section 1)
  LIGHT:          'INFANTRY',        // a weight class of infantry
  SUSTAINMENT:    'LOGISTICS',       // 2026-10-25 (Dr. Tatar): the US name of the logistics brigade (the Army renamed logistics to sustainment in 2006); label 'Sustainment Brigade' for US
  RANGER:         'SPECIAL_FORCES',  // the US label of a light-infantry raid force
  QUARTERMASTER:  'LOGISTICS',       // the US supply branch
  ORDNANCE:       'MAINTENANCE',     // the US ammunition, maintenance and EOD branch (EOD would be a small attached team)
  MEDICAL_BRIGADE: 'MEDICAL',        // the brigade-level medical type already exists
  CONSTRUCTION:   null,
  BRIDGE:         null,
  ARTILLERY_SUPPORT: null
});

/** Keys of the registry that have the given kind. */
export const unitTypesOfKind = (kind) => Object.keys(UNIT_TYPE_REGISTRY).filter((k) => UNIT_TYPE_REGISTRY[k].kinds.includes(kind));

export const isUnitType = (key) => Object.prototype.hasOwnProperty.call(UNIT_TYPE_REGISTRY, key);

/**
 * The registry key a spelling stands for. Throws for a spelling the registry does not know and for an alias that has no unit type
 * (value null), so a misspelled type cannot pass silently.
 */
export function canonicalUnitType(key) {
  if (isUnitType(key)) return key;
  if (Object.prototype.hasOwnProperty.call(UNIT_TYPE_ALIASES, key)) {
    const target = UNIT_TYPE_ALIASES[key];
    if (target === null) throw new Error(`canonicalUnitType: '${key}' is not a unit type (no unit has it); add it to the registry or remove it`);
    return target;
  }
  throw new Error(`canonicalUnitType: unknown unit type '${key}' (registry: ${Object.keys(UNIT_TYPE_REGISTRY).join(', ')})`);
}

/**
 * Translate a known old spelling to its registry key (ENGINEER to ENGINEERS, ARMORED to ARMOR, CAVALRY to RECONNAISSANCE ...), for
 * data that was saved before the rename (a saved scenario, a Supabase row). A registry key and any unknown value are returned
 * unchanged, so a genuinely unknown type still fails loudly downstream; an alias without a unit type (value null) is also returned
 * unchanged.
 */
export function normalizeUnitTypeKey(key) {
  if (typeof key !== 'string') return key;
  if (Object.prototype.hasOwnProperty.call(UNIT_TYPE_ALIASES, key) && UNIT_TYPE_ALIASES[key]) return UNIT_TYPE_ALIASES[key];
  return key;
}

/**
 * Strict read of a table keyed by unit type: a spelling the registry does not know and a registry key the table has no row for
 * both throw (the earlier code returned a silent default: 'STANDARD', SUPPORT_ACTIVE, the MECHANIZED row, 1.0, 0). An old
 * spelling is translated first. Use it where a missing row is a data error, not a choice.
 */
export function lookupByUnitType(table, key, tableName) {
  const k = normalizeUnitTypeKey(key);
  if (!isUnitType(k)) {
    throw new Error(`${tableName}: '${key}' is not a unit type (registry: unitTypeRegistry.js)`);
  }
  if (!Object.prototype.hasOwnProperty.call(table, k)) {
    throw new Error(`${tableName}: no row for unit type '${k}'; add it to the table (do not rely on a default)`);
  }
  return table[k];
}
